import {Component, EventEmitter, HostListener, Injectable, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {CdkDragDrop, moveItemInArray, transferArrayItem} from '@angular/cdk/drag-drop';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';

import {Observable, Subject, Subscription} from 'rxjs';
import {distinctUntilChanged, map, startWith} from 'rxjs/operators';

import parseMax from 'libphonenumber-js/max';

import {IndividualDeleteComponent} from './individual-delete/individual-delete.component';
import {ContactWarningDialogComponent} from './contact-warning/contact-warning.component';
import {ChipPopupComponent, CountryCodeComponent} from 'src/app/components';
import {Contact, ContactType, ContactTypeEnum, Individual, IndividualRequest, Pageable, MessageType} from 'src/app/models';
import {ContactTypeService, DataService, GroupsService, IndividualsService, SnackbarService, MessagesService} from 'src/app/services';
import {ApiKeyComponent} from 'src/app/screens';
import {stringCompare} from '../../../utilities/string';
import {isEmail} from '../../../utilities/email';

import { safeList } from 'src/app/utilities/arrays';

import {TestContactPopupComponent} from './test-contact-popup/test-contact-popup.component';

@Component({
    selector: 'app-individuals',
    templateUrl: './individuals.component.html',
    styleUrls: ['./individuals.component.scss']
})
@Injectable({
    providedIn: 'root'
})
export class IndividualsComponent implements OnInit {
    ContactTypeEnum = ContactTypeEnum;
    contactTypes: ContactType[] = [];
    @Input() userProfile: boolean;
    @Input() individual: Individual;
    @Input() groups: any[] = [];
    @Input() groupsOwned: any[] = [];
    @Input() contactsFound: any[] = [];
    @Output() saved: EventEmitter<any> = new EventEmitter<any>();
    indSelected = false;
    filter = '';
    contactField = '';
    responseRequired = false;
    informational = true;
    admin: boolean;
    error = false;
    nameErrorText = 'No Results Found';
    pageNum = 1;
    isHaveIndividualInfo: boolean;
    currentIndividual: any = new Individual();
    saveValid = true;
    validNumber = true;
    validEmail = true;
    filteredNames: Subscription;
    allIndividualName: string[];
    displayText = '';
    filteredIndividualName: Observable<string[]>;
    associateNames = null;
    associateNamesFormatted: string[] = [];
    isMobileResolution: boolean;
    mainForm = new UntypedFormGroup({
        IndividualsControl: new UntypedFormControl('')
    });
    // owners and membership
    selectable = true;
    removedGroupsOwned: any[] = [];
    removedMembership: any[] = [];
    contactChips: {
        hovering?: boolean;
        displayable: string, value: Contact
    }[];
    contacts: Contact[];
    originalContacts: Contact[];
    contactType = this.ContactTypeEnum.SMS;
    contactOutput = '';
    desiredPrimaryContact: Contact;
    originalPrimaryContact: Contact;
    individualChange = new Subject<string>();
    tooltipPos = ['above', 'right', 'below'];
    @ViewChild('countryCode') countryCode: CountryCodeComponent;
    disabled = false;
    buttonsDisabled = false;
    hourglass = false;
    showResponseRequiredWarning = false;
    currentUser: Individual;
    messageTypesEnabled = false;
    teamsContactEnabled = false;
    hasTeamsContact = false;
    untestedTeamsContacts: Contact[] = [];
    //Message Type Specific Chips for drag & drop
    responseRequiredChips: {
        hovering?: boolean;
        displayable: string;
        value: Contact;
    }[];
    responseRequiredAndInfoChips: {
        hovering?: boolean;
        displayable: string;
        value: Contact;
    }[];
    informationalChips: {
        hovering?: boolean;
        displayable: string;
        value: Contact;
    }[];
    noneChips: {
        hovering?: boolean;
        displayable: string;
        value: Contact;
    }[];

    constructor(public dialog: MatDialog,
                private contactTypeService: ContactTypeService,
                private dataService: DataService,
                private individualsService: IndividualsService,
                private groupsService: GroupsService,
                private snackbarService: SnackbarService,
                private messagesService: MessagesService) {
        this.filteredIndividualName = this.IndividualsControl.valueChanges.pipe(
            startWith(null as boolean),
            map((individual: string | null) => individual ? this._filter(individual) : this.allIndividualName.slice()));
    }

    get IndividualsControl() {
        return this.mainForm.get('IndividualsControl');
    }

    // eslint says it won't trigger this, but it seems to do it anyway so ignoring unless it causes problems
    // eslint-disable-next-line @angular-eslint/contextual-lifecycle
    ngOnInit() {
        this.onResize();

        this.dataService.getSetting("FF_MessageType").subscribe({
            next: (res: boolean) => {
                this.messageTypesEnabled = res;
                this.dataService.getUserInfo().subscribe({
                    next: user => {
                        this.admin = user.isAdmin;

                        if (this.userProfile) {
                            this.currentIndividual = this.individual;
                            this.isHaveIndividualInfo = true;
                            if (this.currentIndividual.group_ownership !== undefined) {
                                this.currentIndividual.group_ownership = this.currentIndividual.group_ownership.sort((n1, n2) => {
                                    return stringCompare(n1.name, n2.name);
                                });
                            }
                            if (this.messageTypesEnabled) {
                                this.updateContactMessageTypesChips(this.currentIndividual.contacts);
                                this.hasResponseRequiredSMS();
                            } else {
                                this.updateContactChips(this.currentIndividual.contacts);
                            }
                        } else {
                            this.isHaveIndividualInfo = false;
                        }

                        this.originalContacts = this.currentIndividual.contacts != undefined ? this.copyContacts(this.currentIndividual.contacts) : [];
                        this.setupOnChangeSearches();

                        this.currentIndividual.contacts.forEach(contact => {
                            if (contact.contact_type === this.ContactTypeEnum.Teams) {
                                this.hasTeamsContact = true;
                                if (!this.currentIndividual.id) {
                                    this.untestedTeamsContacts.push(contact);
                                }
                            }
                        });

                        this.contactTypeService.getContactTypes().subscribe({
                            next: (cTypes: ContactType[]) => {
                                if (this.admin) {
                                    this.contactTypes = cTypes;
                                } else {
                                    this.contactTypes = cTypes.filter(c => !c.admin_only);
                                }

                                this.contactTypes = this.contactTypes.filter(c => c.contact_type !== 'CHAT' && c.contact_type !== 'WEBHOOK');

                                this.dataService.getSetting("FF_TeamsLogicApp").subscribe({
                                    next: (res: boolean) => {
                                        this.teamsContactEnabled = res;
                                        if (!res) {
                                            this.contactTypes = this.contactTypes.filter(c => c.contact_type !== 'TEAMS');
                                        }
                                    }
                                });
                            }, error: () => {
                                this.snackbarService.openSnackBar('Error retrieving contact type information', true);
                            }
                        });
                    }, error: () => {
                        this.snackbarService.openSnackBar('Error retrieving user information', true);
                    }
                });
            }
        });
    }

    setupOnChangeSearches() {
        this.individualChange.pipe(
            distinctUntilChanged()
        ).subscribe((term: string) => this.getAssociatesNames(term));
    }

    onIndividualChanged(event: any) {
        if (event.data === undefined) {
            this.individualChange.next(event);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.isMobileResolution = window.innerWidth < 768;
    }

    toAdd(event): void {
        const value = this.contactField.replace(/[^0-9]+/g, '').trim();
        const formatted_value = `+${this.countryCode.getSelectedCountry()}${value}`;
        const parsedPhone = parseMax(formatted_value);
        if (parsedPhone !== undefined && parsedPhone.isValid()) {
            if (!this.currentIndividual.contacts.find(n => (n.address && parsedPhone.number === n.address && n.contact_type == this.contactType))) {
                if (event.type === 'blur' || event.key === 'Enter') {
                    this.contactField = '';
                    this.add(value);
                }
                this.validNumber = true;
            } else {
                this.displayText = 'Duplicate phone number entered. Please enter another number';
                this.snackbarService.openSnackBar(this.displayText, true);
                this.validNumber = false;
            }
        } else {
            this.displayText = 'Please enter a valid number';
            this.validNumber = false;
        }
    }

    add(event): void {
        const isPhone = this.contactType === 'SMS' || this.contactType === 'VOICE';
        const valueEvent = event.target ? event.target.value : event;
        const formatted_value = isPhone ? `+${this.countryCode.getSelectedCountry()}${event}` : event;
        if (event.key !== undefined && event.key !== 'Enter') {
            return;
        }
        if ((valueEvent || '').trim()) {
            let contact: Contact;
            switch (this.contactType) {
                case 'SMS':
                    const SMSNumber = parseMax(formatted_value);
                    if (SMSNumber) {
                        if (SMSNumber.isValid()) {
                            if (!this.currentIndividual.contacts.find(n => (n.address && SMSNumber.format('E.164') === n.address && n.contact_type == 'SMS'))) {
                                contact = new Contact(1,
                                    ContactTypeEnum.SMS,
                                    this.currentIndividual.contacts.length + 1,
                                    SMSNumber.format('E.164'),
                                    null,
                                    false,
                                    null,
                                    true,
                                    this.calcMessageType(this.responseRequired, this.informational));
                                this.currentIndividual.contacts.push(contact);
                            } else {
                                this.displayText = 'Duplicate phone number entered. Please enter another number';
                                this.snackbarService.openSnackBar(this.displayText, true);
                                this.validNumber = false;
                            }
                        }
                    }
                    break;
                case 'VOICE':
                    const phoneNumber = parseMax(formatted_value);
                    if (phoneNumber && phoneNumber.isValid()) {
                        if (!this.currentIndividual.contacts.find(n => (n.address && phoneNumber.format('E.164') === n.address && n.contact_type == 'VOICE'))) {
                            contact = new Contact(2,
                                ContactTypeEnum.Voice,
                                this.currentIndividual.contacts.length + 1,
                                phoneNumber.format('E.164'),
                                null,
                                false,
                                null,
                                true,
                                this.calcMessageType(this.responseRequired, this.informational));
                            this.currentIndividual.contacts.push(contact);
                        } else {
                            this.displayText = 'Duplicate phone number entered. Please enter another number';
                            this.snackbarService.openSnackBar(this.displayText, true);
                            this.validNumber = false;
                        }
                    }
                    break;
                case 'TEAMS':
                    const msTeamsValue = event.target.value;
                    if (isEmail(msTeamsValue)) {
                        if (!this.currentIndividual.contacts.find(n => (n.address.toUpperCase() && msTeamsValue.toUpperCase() === n.address.toUpperCase()) && n.contact_type === 'TEAMS')) {
                            this.contactField = '';
                            contact = new Contact(6,
                                ContactTypeEnum.Teams,
                                this.currentIndividual.contacts.length + 1,
                                msTeamsValue,
                                null,
                                false,
                                null,
                                true,
                                this.calcMessageType(this.responseRequired, this.informational));
                            this.currentIndividual.contacts.push(contact);
                            this.hasTeamsContact = true;
                            this.untestedTeamsContacts.push(contact);
                        } else {
                            this.snackbarService.openSnackBar('Duplicate Teams address entered. Please enter another address', true);
                            this.validNumber = false;
                        }
                    }
                    break;
                case 'EMAIL':
                    const emailValue = event.target.value;
                    if (isEmail(emailValue)) {
                        if (!this.currentIndividual.contacts.find(n => (n.address.toUpperCase() && emailValue.toUpperCase() === n.address.toUpperCase()) && n.contact_type === 'EMAIL')) {
                            this.contactField = '';
                            this.validEmail = true;
                            contact = new Contact(3,
                                ContactTypeEnum.Email,
                                this.currentIndividual.contacts.length + 1,
                                emailValue,
                                null,
                                false,
                                null,
                                true,
                                this.calcMessageType(this.responseRequired, this.informational));
                            this.currentIndividual.contacts.push(contact);
                        } else {
                            this.snackbarService.openSnackBar('Duplicate email address entered. Please enter another address', true);
                            this.validNumber = false;
                        }
                    } else {
                        this.validEmail = false;
                    }
                    break;
            }
            if (this.messageTypesEnabled) {
                this.updateContactMessageTypesChips(this.currentIndividual.contacts);
            } else {
                this.updateContactChips(this.currentIndividual.contacts);
            }

            if (this.currentIndividual.contacts.length === 1) {
                this.currentIndividual.preferred_contact_id = this.currentIndividual.contacts[0].contact_id;
            }
        }
    }

    updateContactChips(contactList: Contact[]) {
            // TODO: only update needed contact instead of wiping them every time
            this.contactChips = [];
            if (contactList) {
                contactList.forEach(contact => {
                    if (!contact.display_address && contact.contact_type_id === 4) {
                        contact.display_address = this.currentIndividual.associate_email.replace('@', '-').toLowerCase();
                        this.snackbarService.openSnackBar('Mattermost Decommissioned - Contact Will Not Work', true);
                    }
                    this.contactChips.push({
                        displayable: contact.display_address ? contact.display_address : contact.address,
                        value: contact
                    });
                    contact.informational = (contact.message_type === MessageType.RESPONSE_REQUIRED || contact.message_type === MessageType.NONE) ? false : true;
                    contact.response_required = (contact.message_type === MessageType.INFORMATIONAL || contact.message_type === MessageType.NONE) ? false : true;
                });
            }
            if(this.messageTypesEnabled) {
                this.hasResponseRequiredSMS();
            }
        }

    remove(givenContact: Contact): void {
        if (!this.messageTypesEnabled && givenContact.preferred) {
            this.snackbarService.openSnackBar('You can\'t delete your preferred contact!', false);
            return;
        }

        const index = this.currentIndividual.contacts.indexOf(givenContact);
        if (index >= 0) {
            this.currentIndividual.contacts.splice(index, 1);

            if (this.messageTypesEnabled) {
                if (givenContact.preferred) {
                    // Preferred contacts aren't visible with FF_MessageType enabled, but they still exist in the code
                    // If the deleted contact is preferred, switch it to a different contact to allow seamless deletion
                    // while not leaving data in a bad state if FF_MessageType is disabled
                    const replacement = this.currentIndividual.contacts.find(c => c.active)
                        ?? this.currentIndividual.contacts[0];
                    if (replacement) {
                        this.currentIndividual.contacts.forEach(c => c.preferred = false);
                        replacement.preferred = true;
                        this.currentIndividual.preferred_contact_id = replacement.id;
                    } else {
                        this.currentIndividual.preferred_contact_id = null;
                    }
                }

                // V2 flow: organize by message type
                this.updateContactMessageTypesChips(this.currentIndividual.contacts);
            } else {
                // V1 flow: sort by priority
                this.sortContacts(this.currentIndividual.contacts);
                this.updateContactChips(this.currentIndividual.contacts);
            }
        }

        if (givenContact.contact_type === this.ContactTypeEnum.Teams) {
            let indexToRemove = this.untestedTeamsContacts.indexOf(givenContact);
            if (indexToRemove !== -1) {
                this.untestedTeamsContacts.splice(indexToRemove, 1);
            }
        }
    }

    removeOwnedGroup(group) {
        this.groupsService.getGroupOwners(group.id).subscribe(ret => {
            if (ret.length > 1) {
                this.currentIndividual.group_ownership = this.currentIndividual.group_ownership.filter(p => p.id !== group.id);
                this.removedGroupsOwned.push(group);
            } else {
                this.snackbarService.openSnackBar(`You are the only owner on ${group.name}`, true);
            }
        });
    }

    removeMembership(group) {
        this.groupsService.getGroup(group.id).subscribe(grp => {
            if (grp.members.length > 1) {
                this.currentIndividual.group_membership = this.currentIndividual.group_membership.filter(p => p.id !== group.id);
                this.removedMembership.push(group);
            } else {
                this.snackbarService.openSnackBar(`You are the only member in ${group.name}`, true);
            }
        });
    }

    drop(event: CdkDragDrop<string[]>) {
        if (event.previousContainer !== event.container && this.messageTypesEnabled) {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex,
            );
            switch (event.container.element.nativeElement.accessKey) {
                case "responseRequired":
                    this.responseRequiredChips.forEach(contactChip => {
                        contactChip.value.message_type = MessageType.RESPONSE_REQUIRED;
                    });
                    break;
                case "responseRequiredAndInfo":
                    this.responseRequiredAndInfoChips.forEach(contactChip => {
                        contactChip.value.message_type = MessageType.RESPONSE_REQUIRED_AND_INFORMATIONAL;
                    });
                    break;
                case "informational":
                    this.informationalChips.forEach(contactChip => {
                        contactChip.value.message_type = MessageType.INFORMATIONAL;
                    });
                    break;
                case "none":
                    this.noneChips.forEach(contactChip => {
                        contactChip.value.message_type = MessageType.NONE;
                    });
                    break;
            }
            this.contactChips = [...this.responseRequiredChips, ...this.responseRequiredAndInfoChips, ...this.informationalChips, ...this.noneChips];
        } else {
            moveItemInArray(this.currentIndividual.contacts, event.previousIndex, event.currentIndex);
            this.sortContacts(this.currentIndividual.contacts);
        }
    }

    sortContacts(contacts: Contact[]) {
        let index = 1;
        contacts.forEach(contact => contact.priority = index++);
        this.updateContactChips(contacts);
    }

    updateContactMessageTypesChips(contacts: Contact[]) {
        this.responseRequiredChips = [];
        this.responseRequiredAndInfoChips = [];
        this.informationalChips = [];
        this.noneChips = [];

        if(contacts) {
            contacts.forEach(contact => {
                if (!contact.display_address && contact.contact_type_id === 4) {
                    contact.display_address = this.currentIndividual.associate_email.replace('@', '-').toLowerCase();
                    this.snackbarService.openSnackBar('Mattermost Decommissioned - Contact Will Not Work', true);
                }
                switch (contact.message_type) {
                    case 'RESPONSE_REQUIRED':
                        this.responseRequiredChips.push({
                            displayable: contact.display_address ? contact.display_address : contact.address,
                            value: contact
                        });
                        break;
                    case 'RESPONSE_REQUIRED_AND_INFORMATIONAL':
                        this.responseRequiredAndInfoChips.push({
                            displayable: contact.display_address ? contact.display_address : contact.address,
                            value: contact
                        });
                        break;
                    case 'INFORMATIONAL':
                        this.informationalChips.push({
                            displayable: contact.display_address ? contact.display_address : contact.address,
                            value: contact
                        });
                        break;
                    case 'NONE':
                        this.noneChips.push({
                            displayable: contact.display_address ? contact.display_address : contact.address,
                            value: contact
                        });
                        break;
                }
            });
            this.contactChips = [...this.responseRequiredChips, ...this.responseRequiredAndInfoChips, ...this.informationalChips, ...this.noneChips];
        }
    }

    selected(event: MatAutocompleteSelectedEvent): void {
        const search = event.option.value.slice(event.option.value.lastIndexOf('(') + 1, event.option.value.lastIndexOf(')'));
        const ind = this.associateNames.find(e => e.external_id === search);
        this.cancel();
        this.findIndividual(ind);
    }

    findIndividual(associateInfo): Individual {
        // Clear search field
        this.IndividualsControl.setValue('');
        if (this.countryCode) {
            this.countryCode.initCC = true;
        }
        // Translate AssociateDir response into Individual object
        this.currentIndividual = associateInfo;
        this.isHaveIndividualInfo = true;
        if (this.messageTypesEnabled) {
            this.updateContactMessageTypesChips(associateInfo.contacts);
        } else {
            this.updateContactChips(associateInfo.contacts);
        }
        if (this.currentIndividual.group_ownership !== undefined) {
            this.currentIndividual.group_ownership = this.currentIndividual.group_ownership.sort((n1, n2) => {
                return stringCompare(n1.name, n2.name);
            });
        }
        this.updateType(this.ContactTypeEnum.SMS);
        return this.currentIndividual;
    }

    public isSMS(contact): boolean {
        return contact.contact_type.toUpperCase() === this.ContactTypeEnum.SMS.toUpperCase();
    }

    public isEMail(contact): boolean {
        return contact.contact_type.toUpperCase() === this.ContactTypeEnum.Email.toUpperCase();
    }

    public isVoice(contact): boolean {
        return contact.contact_type.toUpperCase() === this.ContactTypeEnum.Voice.toUpperCase();
    }

    public isChat(contact): boolean {
        return contact.contact_type.toUpperCase() === this.ContactTypeEnum.Chat.toUpperCase();
    }

    public isTeams(contact): boolean {
        return contact.contact_type.toUpperCase() === this.ContactTypeEnum.Teams.toUpperCase();
    }

    changePreferred(contact: Contact): void {
        if (!contact.active) {
            this.snackbarService.openSnackBar('Inactive contacts cannot be set as your preferred contact.', true);
        }

        this.contactChips.map(chip => {
            chip.value.preferred = false;
            return chip;
        });
        this.desiredPrimaryContact = contact;
        contact.preferred = true;
    }

    updateType(event: any): void {
        this.contactField = '';
        this.validEmail = true;
        this.validNumber = true;
        this.contactType = event.value || event || this.ContactTypeEnum.SMS;
        switch (event.value || event) {
            case this.ContactTypeEnum.Voice:
                this.contactOutput = 'A Phone Number';
                break;
            case this.ContactTypeEnum.Email:
                this.contactOutput = 'An Email';
                break;
            case this.ContactTypeEnum.Teams:
                this.contactOutput = 'An Email linked to a MS Teams account';
                break;
            case this.ContactTypeEnum.SMS:
            default:
                this.contactOutput = 'A Phone Number (for an SMS enabled device such as a Smart Phone)';
        }
        if (this.contactTypes) {
            for (var cType of safeList(this.contactTypes)) {
                if (cType.contact_type?.toUpperCase() === event.value?.toUpperCase()) {
                    if (cType.admin_only) {
                        this.showContactWarning(cType.description);
                    }
                    break;
                }
            }
        }
    }

    getFilterName(ind: Individual) {
        return `${(ind.id ? ' ' : '⚠️')}${ind.full_name} (${ind.external_id})`;
    }

    // Get Individuals names in autocomplete code

    getAssociatesNames(filterValueName: string) {
        if ((this.currentIndividual && this.getFilterName(this.currentIndividual) === filterValueName) || filterValueName === "") {
            return;
        }
        this.hourglass = true;
        this.error = false;

        if (filterValueName != null && filterValueName.length > 1) {
            this.filter = filterValueName;
            this.filteredNames = this.dataService.getIndividualAndAssociateNames(filterValueName).subscribe({
                next: (names: Pageable<Individual[]>) => {
                    this.associateNames = names.content;
                    this.associateNamesFormatted = [];
                    this.associateNames.forEach(e => this.associateNamesFormatted.push(this.getFilterName(e)));
                    this.hourglass = false;
                }, error: err => {
                    this.error = true;
                    if (err['status'] === 404) {
                        this.nameErrorText = 'No results found';
                    } else {
                        this.nameErrorText = 'There was an issue accessing Associate Directory';
                    }
                    this.associateNamesFormatted = [];
                    this.isHaveIndividualInfo = false;
                    this.hourglass = false;
                }
            });
        } else {
            this.associateNames = [];
            this.associateNamesFormatted = [];
        }
    }

    toggleIconButton(input: string) {
        switch (input) {
            case 'informational':
                if (this.informational === true && this.responseRequired === false) {
                    this.responseRequired = true;
                }
                this.informational = (this.informational === false) ? true : false;
                break;
            case 'responseRequired':
                if (this.responseRequired === true && this.informational === false) {
                    this.informational = true;
                }
                this.responseRequired = (this.responseRequired === false) ? true : false;
                break;
        }
    }

    load(page) {
        if (page.data === undefined) {
            this.pageNum = page;
            this.dataService.getIndividualAndAssociateNamesPage(this.filter, page * 15).subscribe((names: Pageable<Individual>) => {
                this.associateNames = names.content;
                this.associateNamesFormatted = [];
                this.associateNames.forEach(e => this.associateNamesFormatted.push(this.getFilterName(e)));
            });
        }
    }

    save() {
        if (this.messageTypesEnabled){
            this.contactChips = [...this.responseRequiredChips, ...this.responseRequiredAndInfoChips, ...this.informationalChips, ...this.noneChips];
        }

        this.buttonsDisabled = true;
        this.saveValid = this.currentIndividual.contacts.length !== 0;

        // Check One Contact is Active
        if (!this.contactChips.find(contact => contact.value.active === true)) {
            this.snackbarService.openSnackBar('At least one contact must be active.', true);
            this.saveValid = false;
        }

        // Check Primary Contact is Active
        if (this.desiredPrimaryContact) {
            // Check New Primary Contact is Active
            if (this.desiredPrimaryContact.active) {
                this.currentIndividual.contacts.forEach(element => element.preferred = false);
                this.desiredPrimaryContact.preferred = true;
                this.currentIndividual.preferred_contact_id = this.desiredPrimaryContact.id;
            } else {
                this.snackbarService.openSnackBar('Inactive contacts cannot be set as your preferred contact.', true);
                this.saveValid = false;
            }
        } else {
            // Check Old Primary Contact is Active
            let preferredContact = this.contactChips.find(contact => contact.value.preferred === true);
            if (!preferredContact.value.active) {
                this.snackbarService.openSnackBar('Inactive contacts cannot be set as your preferred contact.', true);
                this.saveValid = false;
            }
        }

        if (this.saveValid) {
            !this.currentIndividual.id ? this.addIndividual(this.currentIndividual) : this.updateIndividual(this.currentIndividual);
        } else {
            this.buttonsDisabled = false;
        }
        setTimeout(() => this.saved.emit(this.currentIndividual), 10);
    }

    isMessageTypeResponseRequired(message_type: MessageType): boolean {
        return message_type === MessageType.RESPONSE_REQUIRED || message_type === MessageType.RESPONSE_REQUIRED_AND_INFORMATIONAL;
    }

    isTeamsOrSMS(contact_type: ContactTypeEnum): boolean {
        return contact_type === ContactTypeEnum.Teams || contact_type === ContactTypeEnum.SMS
    }

    hasResponseRequiredSMS() {
        // Prefer the API's authoritative check on saved profiles — it applies the same rule as
        // the save-time validation (admin callers accept any contact type; non-admins require
        // SMS or Teams). Fall back to a client-side check when the individual hasn't been
        // persisted yet (new profile) or after in-memory chip drags where the server doesn't
        // know about the pending change.
        if (this.currentIndividual?.id) {
            this.individualsService.hasResponseRequiredContact(this.currentIndividual.id).subscribe({
                next: (hasIt: boolean) => this.showResponseRequiredWarning = !hasIt,
                error: () => this.showResponseRequiredWarning = !this.clientHasResponseRequiredContact()
            });
        } else {
            this.showResponseRequiredWarning = !this.clientHasResponseRequiredContact();
        }
    }

    private clientHasResponseRequiredContact(): boolean {
        return !!this.contactChips.find(contact =>
            this.isMessageTypeResponseRequired(contact.value.message_type) && this.isTeamsOrSMS(contact.value.contact_type));
    }

    calcMessageType(responseRequired: boolean, informational: boolean): MessageType {
        if (responseRequired) {
            if (informational) {
                return MessageType.RESPONSE_REQUIRED_AND_INFORMATIONAL;
            }
            return MessageType.RESPONSE_REQUIRED;
        } else if (informational) {
            return MessageType.INFORMATIONAL;
        }
        return MessageType.NONE;
    }

    updateMessageType(contact: Contact) {
        contact.message_type = this.calcMessageType(contact.response_required, contact.informational);
    }

    addIndividual(individual: IndividualRequest) {
        this.doIndividualUpdates(individual, 'createIndividual', 'Adding individual', 'Individual Added Successfully');
    }

    updateIndividual(individual: IndividualRequest) {
        this.doIndividualUpdates(individual, 'updateIndividual', 'Updating individual', 'Individual Updated Successfully');
    }

    doIndividualUpdates(request: IndividualRequest, method: string, doingMessage: string, doneMessage: string) {
        this.removedGroupsOwned.forEach((group) => {
            this.groupsService.deleteGroupOwner(group.id, this.currentIndividual.external_id).subscribe(() => this.buttonsDisabled = false);
        });
        this.removedMembership.forEach((group) => {
            this.groupsService.removeGroupMember(group.id, this.currentIndividual.external_id).subscribe(() => this.buttonsDisabled = false);
        });
        this.hourglass = true;
        this.individualsService[method](request).subscribe({
            next: () => {
                this.buttonsDisabled = false;
                if (this.userProfile) {
                    this.dataService.getUserInfo().subscribe({
                        next: (userInfo) => {
                            const userID = userInfo['email'];
                            this.dataService.getIndividualAndAssociateExactMatchLookup(userID).subscribe((res) => {
                                this.currentIndividual = res.content[0];
                                if (this.messageTypesEnabled) {
                                    this.updateContactMessageTypesChips(this.currentIndividual.contacts);
                                } else {
                                    this.updateContactChips(this.currentIndividual.contacts);
                                }
                            }).add(() => this.hourglass = false);
                        }, error: () => () => this.hourglass = false
                    });
                } else {
                    this.hourglass = false;
                }
                this.snackbarService.openSnackBar(doneMessage, false);
                location.reload();
            }, error: err => {
                // If the error was caused by invalid input, replace the default format with a user-friendly explanation
                let errorMessage;
                switch (err?.error?.message ?? '<unknown error>') {
                    case "400 BAD_REQUEST \"Response required SMS or Teams contact is required.\"":
                        errorMessage = "You must have at least one SMS or Teams contact set to either 'Response Required' or 'Response Required and Informational'";
                        break;
                    case "400 BAD_REQUEST \"An Informational Email or Teams contact is required.\"":
                        errorMessage = "You must have at least one Email or Teams contact set to either 'Informational' or 'Response Required and Informational'";
                        break;
                    default:
                        errorMessage = 'Individual Could Not Be Added Or Updated. ERROR: ' + err.error.status + ': ' + err.error.message;
                }
                this.snackbarService.openSnackBar(errorMessage, true);
            }
        }).add(() => this.cancel());
    }

    cancel() {
        if (!this.userProfile) {
            this.IndividualsControl.setValue('');
            this.isHaveIndividualInfo = false;
            this.currentIndividual = null;
            this.contactType = this.ContactTypeEnum.SMS;
            this.contactField = '';
            this.validNumber = true;
            this.validEmail = true;
            this.groupsOwned = [];
            this.associateNames = [];
            this.associateNamesFormatted = [];
        } else {
            this.currentIndividual.contacts = this.originalContacts != undefined ? this.copyContacts(this.originalContacts) : [];
            this.individual.contacts = this.originalContacts != undefined ? this.copyContacts(this.originalContacts) : [];
            if (this.messageTypesEnabled) {
                this.updateContactMessageTypesChips(this.currentIndividual.contacts);
            } else {
                this.updateContactChips(this.currentIndividual.contacts);
            }
        }
        this.buttonsDisabled = false;
        this.hourglass = false;
        this.untestedTeamsContacts = [];
        this.hasTeamsContact = false;
    }

    deleteIndividual() {
        this.buttonsDisabled = true;
        const dialogRef = this.dialog.open(IndividualDeleteComponent, {
            width: '325px',
            height: '205px',
            panelClass: 'confirmation',
            data: {full_name: this.currentIndividual.full_name}
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.hourglass = true;
                this.individualsService.deleteIndividual(this.currentIndividual.id).subscribe({
                    next: () => {
                        this.cancel();
                        this.snackbarService.openSnackBar('Individual Deleted Successfully', false);
                    }, error: () => this.snackbarService.openSnackBar('ERROR: Individual may not be deleted', true)
                }).add(() => {
                    this.buttonsDisabled = false;
                    this.hourglass = false;
                });
            } else {
                this.buttonsDisabled = false;
            }
        });
    }

    copyContacts(list1: Contact[]) {
        var list2: Contact[] = [];
        var copy;
        list1.forEach(contact => {
            copy = new Contact(contact.contact_type_id,
               contact.contact_type,
               contact.priority,
               contact.address,
               contact.display_address,
               contact.preferred,
               contact.id,
               contact.active,
               contact.message_type);
            list2.push(copy);
        });
        return list2;
    }

    showContactWarning(description: string) {
        this.buttonsDisabled = true;
        this.dialog.open(ContactWarningDialogComponent, {
            width: '565px',
            height: '250px',
            panelClass: 'contact-warning',
            data: description
        }).afterClosed().subscribe({
            next: () => {
                this.buttonsDisabled = false;
            }, error: () => {
                this.snackbarService.openSnackBar('Error adding contact. Please contact an admin if your issue continues.', false);
            }
        });
    }


    reactivateContacts() {
        this.currentIndividual.active = !this.currentIndividual.active;
        this.currentIndividual.contacts.forEach(contact => contact.active = true);
        this.individualsService.updateIndividual(this.currentIndividual);
        if (this.currentIndividual.active === false) {
            this.buttonsDisabled = false;
        }
    }

    isDisabled(): boolean {
        return this.disabled || !this.currentIndividual.active || !this.currentIndividual.id;
    }

    openGroupPopup(groupID: any) {
        this.groupsService.getGroup(groupID).subscribe(group => {
            this.dialog.open(ChipPopupComponent, {
                width: '85%',
                maxHeight: '90%',
                data: {object: group, type: 'GROUP'}
            });
        });
    }

    keyPopup() {
        this.dialog.open(ApiKeyComponent, {
            width: '50%'
        });
    }

    ordinal(address) {
        return this.originalContacts.findIndex(function(el) {
            return el.address === address;
        });
    }

    testContact() {
        if (this.messageTypesEnabled){
            this.contactChips = [...this.responseRequiredChips, ...this.responseRequiredAndInfoChips, ...this.informationalChips, ...this.noneChips];
        }

        let teamsContactChips = this.contactChips.filter((contactChip) => contactChip.value.contact_type === this.ContactTypeEnum.Teams);

        let testContactPopup = this.dialog.open(TestContactPopupComponent, {
            width: '30%', maxHeight: '90%', data: teamsContactChips
        });

        testContactPopup.afterClosed().subscribe((res) => {
            if (res) {
                this.messagesService.validateTeamsAddress(res).subscribe({
                    next: (success: boolean) => {
                        if (success) {
                            this.snackbarService.openSnackBar('Test message was sent successfully', false);

                            let indexToRemove = this.untestedTeamsContacts.findIndex((contact) => contact.address === res)
                            if (indexToRemove !== -1) {
                                this.untestedTeamsContacts.splice(indexToRemove, 1);
                            }
                        } else {
                            this.snackbarService.openSnackBar('Test message failed to send', true);
                        }
                    }
                });
            }
        });
    }

    private _filter(value: string): string[] {
        const filterValue = value.toLowerCase();
        return this.associateNames.filter(individual => individual.toLowerCase().indexOf(filterValue) === 0);
    }
}
